import clsx from 'clsx';
import React, { type PropsWithChildren, useCallback } from 'react';

type Option<T extends string> = { label: string; value: T };

type TabSize = 'single' | 'double';

type TabV2Props<T extends string> = Pick<Option<T>, 'value'> & {
  selected?: boolean;
  onClick: (value: T) => void;
  size?: TabSize;
  borderColor?: 'main-200' | 'main-300';
};

const TabV2 = <T extends string>({
  children,
  value,
  onClick,
  selected = false,
  size = 'single',
  borderColor,
}: PropsWithChildren<TabV2Props<T>>) => {
  const border = borderColor ?? (size === 'double' ? 'main-300' : 'main-200');
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={clsx(
        'flex',
        size === 'double'
          ? 'items-start pt-f1 h-f8 px-[13.5px]'
          : 'items-center px-f1 py-f1',
        'text-xmd',
        selected
          ? 'bg-transparent text-main'
          : clsx(
              'bg-transparent text-main-500 border-b-[0.5px]',
              border === 'main-300' ? 'border-main-300' : 'border-main-200'
            ),
        'hover:text-main hover:bg-main-100 hover:cursor-pointer',
        'last:border-r-[0.5px] border-l-[0.5px] first:border-l-0',
        border === 'main-300' ? 'border-main-300' : 'border-main-200',
        'transition-colors',
        'box-border'
      )}
      data-testid={value}
    >
      {children}
    </button>
  );
};

type SortProps = {
  label: string;
  order: 'asc' | 'desc';
  onToggle: () => void;
};

type TabsV2Props<T extends string> = {
  options: Option<T>[] | readonly Option<T>[];
  selectedValue?: T;
  onClick: (val: T) => void;
  extraClassNames?: string;
  'data-testid'?: string;
  sort?: SortProps;
  size?: TabSize;
  borderColor?: 'main-200' | 'main-300';
};

export const TabsV2 = <T extends string>({
  options,
  'data-testid': dataTestId,
  selectedValue = undefined,
  extraClassNames,
  onClick,
  sort,
  size = 'single',
  borderColor,
}: TabsV2Props<T>) => {
  const onClickHandler = useCallback(
    (value: T) => {
      onClick(value);
    },
    [onClick]
  );

  return (
    <div
      data-testid={dataTestId}
      className={clsx(
        'flex items-end box-border',
        sort ? 'w-full' : 'w-fit overflow-auto scrollbar-hide truncate',
        extraClassNames
      )}
    >
      <div className="flex">
        {options.map((opt) => (
          <TabV2
            key={opt.value}
            value={opt.value}
            onClick={onClickHandler}
            selected={opt.value === selectedValue}
            size={size}
            borderColor={borderColor}
          >
            {opt.label}
          </TabV2>
        ))}
      </div>
      {sort && (
        <>
          <div
            className={clsx(
              'flex-1 border-b-[0.5px]',
              (borderColor ?? (size === 'double' ? 'main-300' : 'main-200')) ===
                'main-300'
                ? 'border-main-300'
                : 'border-main-200'
            )}
          />
          <button
            type="button"
            onClick={sort.onToggle}
            className={clsx(
              'flex items-center gap-x-0.5 text-xmd text-main-500 hover:text-main border-b-[0.5px]',
              size === 'double'
                ? 'px-[13.5px] h-f8 border-main-300'
                : 'px-f1 py-f1 border-main-200'
            )}
          >
            {sort.label}
            <span>{sort.order === 'desc' ? '↓' : '↑'}</span>
          </button>
        </>
      )}
    </div>
  );
};
